import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Input, Space, Tag, Card } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface InventoryItem {
    id: number;
    itemCode: string;
    itemName: string;
    itemTypeCode: string;
    stockLevel: { currentStockLevel: number };
    prices: { basePrice: number; currencyCode: string }[];
    isActive: boolean;
    createdAt: string;
}

export const InventoryList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "inventory",
    });

    useEffect(() => {
        if (!data?.data) return;

        let items = data.data as InventoryItem[];
        
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            items = items.filter((item: InventoryItem) =>
                item.itemName?.toLowerCase().includes(searchLower) ||
                item.itemCode?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...items].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns: ColumnsType<InventoryItem> = [
        {
            title: "Item Code",
            dataIndex: "itemCode",
            key: "itemCode",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
            sorter: (a, b) => a.itemCode.localeCompare(b.itemCode),
        },
        {
            title: "Name",
            dataIndex: "itemName",
            key: "itemName",
            render: (value: string) => (
                <span className="text-text-secondary">{value}</span>
            ),
            sorter: (a, b) => a.itemName.localeCompare(b.itemName),
        },
        {
            title: "Stock",
            key: "stock",
            render: (_: any, record: InventoryItem) => {
                const stock = record.stockLevel?.currentStockLevel || 0;
                return (
                    <span className={stock <= 0 ? "text-danger font-medium" : stock < 10 ? "text-warning font-medium" : "text-text-secondary"}>
                        {stock.toFixed(0)}
                    </span>
                );
            },
            sorter: (a, b) => (a.stockLevel?.currentStockLevel || 0) - (b.stockLevel?.currentStockLevel || 0),
        },
        {
            title: "Price",
            key: "price",
            render: (_: any, record: InventoryItem) => {
                const price = record.prices?.[0];
                return price ? (
                    <span className="font-semibold text-primary">
                        {price.currencyCode || '$'} {price.basePrice?.toFixed(2)}
                    </span>
                ) : <span className="text-text-muted">-</span>;
            },
            responsive: ['md'],
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['lg'],
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Inventory"
                icon={<AppstoreOutlined />}
                breadcrumbs={[{ title: "Inventory" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/inventory/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by item name or code"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};

export default InventoryList;
